import { Controller } from "@hotwired/stimulus"

// Handles the bulk user→workflow assignment page.
//
// State:
//   this.selectedUsers  = Map<userId, { id, name, email }>
//   this.searchUserList = Array of users from last search (for "Select all")
//
// Flow:
//   1. Search users → results appear with checkboxes.
//   2. Check individual rows or "Select all" → chips appear in the selected panel.
//   3. Check one or more workflow checkboxes on the right.
//   4. Click Assign → POST bulk_create → show result banner.

export default class extends Controller {
  static targets = [
    // User search
    "searchInput",
    "searchResultsWrapper",
    "searchResults",
    "resultCountLabel",
    "noResultsMsg",
    // Selected users
    "selectedUsersLabel",
    "selectedUserChips",
    "userCount",
    // Workflows
    "workflowCheckbox",
    "workflowCount",
    // Bottom bar
    "summary",
    "assignBtn",
    "successMsg",
    "errorMsg",
  ]

  static values = {
    searchUrl:      String,   // GET  /app_configurations/search_users
    bulkCreateUrl:  String,   // POST /app_configuration_user_pairs/bulk_create
  }

  connect() {
    this.selectedUsers  = new Map()  // userId → { id, name, email }
    this.searchUserList = []
    this._debounce      = null
  }

  // ── User search ────────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._debounce)
    const q = this.searchInputTarget.value.trim()
    if (q.length < 2) {
      this._hideSearchResults()
      return
    }
    this._debounce = setTimeout(() => this._doSearch(q), 250)
  }

  async _doSearch(q) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('q', q)
      // Pass 0 so the endpoint doesn't filter out already-assigned users
      // (user might want to assign same person to a different workflow)
      url.searchParams.set('app_configuration_id', 0)

      const res   = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const users = await res.json()

      this.searchUserList = users
      this._renderSearchResults(users)
    } catch (err) {
      this.searchResultsTarget.innerHTML =
        `<div class="p-2 text-danger small">Error: ${err.message}</div>`
      this.searchResultsWrapperTarget.classList.remove('d-none')
    }
  }

  _renderSearchResults(users) {
    this.noResultsMsgTarget.classList.add('d-none')

    if (users.length === 0) {
      this.searchResultsWrapperTarget.classList.add('d-none')
      this.noResultsMsgTarget.classList.remove('d-none')
      return
    }

    this.resultCountLabelTarget.textContent =
      `${users.length} user${users.length === 1 ? '' : 's'} found`

    this.searchResultsTarget.innerHTML = users.map(u => {
      const checked = this.selectedUsers.has(u.id) ? 'checked' : ''
      return `
        <label class="d-flex align-items-center gap-2 px-2 py-1 border-bottom"
               style="cursor:pointer;">
          <input type="checkbox" class="form-check-input mt-0"
                 ${checked}
                 data-user-id="${u.id}"
                 data-user-name="${this._esc(u.name)}"
                 data-user-email="${this._esc(u.email)}"
                 data-action="change->workflow-bulk-assign#toggleUser">
          <span class="small">
            <span class="fw-semibold">${this._esc(u.name)}</span>
            <span class="text-muted ms-1">${this._esc(u.email)}</span>
          </span>
        </label>`
    }).join('')

    this.searchResultsWrapperTarget.classList.remove('d-none')
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this._hideSearchResults()
    this.searchUserList = []
  }

  _hideSearchResults() {
    this.searchResultsWrapperTarget.classList.add('d-none')
    this.noResultsMsgTarget.classList.add('d-none')
  }

  // ── Toggle a single user in/out of selection ───────────────────────────────

  toggleUser(event) {
    const cb    = event.currentTarget
    const id    = parseInt(cb.dataset.userId, 10)
    const name  = cb.dataset.userName
    const email = cb.dataset.userEmail

    if (cb.checked) {
      this.selectedUsers.set(id, { id, name, email })
    } else {
      this.selectedUsers.delete(id)
    }

    this._renderChips()
    this._updateUserCount()
    this._updateSummary()
  }

  // ── Select all users from the current search results ──────────────────────

  selectAllResults() {
    this.searchUserList.forEach(u => {
      this.selectedUsers.set(u.id, u)
    })

    // Tick all checkboxes in the results panel
    this.searchResultsTarget
      .querySelectorAll('input[type="checkbox"]')
      .forEach(cb => { cb.checked = true })

    this._renderChips()
    this._updateUserCount()
    this._updateSummary()
  }

  // ── Render selected-user chips ─────────────────────────────────────────────

  _renderChips() {
    const chips = this.selectedUserChipsTarget

    if (this.selectedUsers.size === 0) {
      chips.innerHTML = ''
      this.selectedUsersLabelTarget.style.display = 'none'
      return
    }

    this.selectedUsersLabelTarget.style.display = ''

    chips.innerHTML = Array.from(this.selectedUsers.values()).map(u => `
      <span class="badge bg-primary d-flex align-items-center gap-1"
            style="font-size:0.8em; font-weight:normal;">
        ${this._esc(u.name)}
        <button type="button"
                class="btn-close btn-close-white"
                style="font-size:0.6em;"
                data-user-id="${u.id}"
                data-action="click->workflow-bulk-assign#removeChip"
                aria-label="Remove">
        </button>
      </span>`
    ).join('')
  }

  removeChip(event) {
    const id = parseInt(event.currentTarget.dataset.userId, 10)
    this.selectedUsers.delete(id)

    // Untick the checkbox in results if still visible
    const cb = this.searchResultsTarget
      .querySelector(`input[data-user-id="${id}"]`)
    if (cb) cb.checked = false

    this._renderChips()
    this._updateUserCount()
    this._updateSummary()
  }

  _updateUserCount() {
    const n = this.selectedUsers.size
    this.userCountTarget.textContent = `${n} selected`
    this.userCountTarget.classList.toggle('bg-primary', n > 0)
    this.userCountTarget.classList.toggle('bg-secondary', n === 0)
  }

  // ── Workflow checkbox count ────────────────────────────────────────────────

  updateWorkflowCount() {
    this._updateWorkflowCountDisplay()
    this._updateSummary()
  }

  selectAllWorkflows() {
    this.workflowCheckboxTargets.forEach(cb => { cb.checked = true })
    this._updateWorkflowCountDisplay()
    this._updateSummary()
  }

  clearWorkflows() {
    this.workflowCheckboxTargets.forEach(cb => { cb.checked = false })
    this._updateWorkflowCountDisplay()
    this._updateSummary()
  }

  _selectedWorkflows() {
    return this.workflowCheckboxTargets.filter(cb => cb.checked)
  }

  _updateWorkflowCountDisplay() {
    const n = this._selectedWorkflows().length
    this.workflowCountTarget.textContent = `${n} selected`
    this.workflowCountTarget.classList.toggle('bg-primary', n > 0)
    this.workflowCountTarget.classList.toggle('bg-secondary', n === 0)
  }

  // ── Summary bar ───────────────────────────────────────────────────────────

  _updateSummary() {
    const u = this.selectedUsers.size
    const w = this._selectedWorkflows().length
    const pairs = u * w

    if (u === 0 && w === 0) {
      this.summaryTarget.textContent = 'Select users and workflows above to assign.'
    } else {
      this.summaryTarget.innerHTML =
        `<strong>${u}</strong> user${u === 1 ? '' : 's'} × ` +
        `<strong>${w}</strong> workflow${w === 1 ? '' : 's'} = ` +
        `<strong>${pairs}</strong> assignment${pairs === 1 ? '' : 's'}`
    }

    const ready = u > 0 && w > 0
    this.assignBtnTarget.disabled = !ready
    this.assignBtnTarget.textContent = ready
      ? `Assign ${pairs} pair${pairs === 1 ? '' : 's'}`
      : 'Assign'
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async assign() {
    const userIds     = Array.from(this.selectedUsers.keys())
    const configIds   = this._selectedWorkflows().map(cb => parseInt(cb.value, 10))

    if (!userIds.length || !configIds.length) return

    const btn = this.assignBtnTarget
    btn.disabled  = true
    btn.textContent = 'Saving…'
    this.successMsgTarget.classList.add('d-none')
    this.errorMsgTarget.classList.add('d-none')

    try {
      const res = await fetch(this.bulkCreateUrlValue, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept:         'application/json',
          'X-CSRF-Token': this._csrf(),
        },
        body: JSON.stringify({ user_ids: userIds, app_configuration_ids: configIds }),
      })

      const data = await res.json()

      if (res.ok && data.success) {
        let msg = `✓ ${data.created} pair${data.created === 1 ? '' : 's'} created.`
        if (data.skipped > 0)
          msg += ` ${data.skipped} already existed (skipped).`

        this.successMsgTarget.textContent = msg
        this.successMsgTarget.classList.remove('d-none')

        if (data.errors?.length) {
          this.errorMsgTarget.textContent = `Errors: ${data.errors.join('; ')}`
          this.errorMsgTarget.classList.remove('d-none')
        }

        // Reset selections after a successful assign
        this.selectedUsers = new Map()
        this.workflowCheckboxTargets.forEach(cb => { cb.checked = false })
        this._renderChips()
        this._updateUserCount()
        this._updateWorkflowCountDisplay()
        this._updateSummary()
        this.clearSearch()
      } else {
        this.errorMsgTarget.textContent = `Error: ${data.error || 'Unknown error'}`
        this.errorMsgTarget.classList.remove('d-none')
      }
    } catch (err) {
      this.errorMsgTarget.textContent = `Request failed: ${err.message}`
      this.errorMsgTarget.classList.remove('d-none')
    } finally {
      btn.disabled    = false
      btn.textContent = 'Assign'
      this._updateSummary()
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
